/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ArrayExtensions, getInvoices, GetInvoicesInput } from '@msdyn365-commerce-modules/retail-actions';
import { Button, Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync } from '@msdyn365-commerce/core';
import { InvoicePaidStatus, InvoiceSearchCriteria, InvoiceType, OrderInvoice } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import * as React from 'react';
import { InvoiceErrorComponent } from './components/invoice-error-component';
import { InvoicesFilterComponent, InvoicesFilterState } from './components/invoices-filter-component';
import { InvoicesTableComponent } from './components/invoices-table/invoices-table-component';
import { IMakePaymentResources, MakePaymentComponent } from './components/make-payment-component';
import { InvoiceModel } from './helpers/invoice-data';
import { IAddInvoicesToCartFailureResult } from './helpers/pay-invoices';
import { IInvoicesListData } from './invoices-list.data';
import { IInvoicesListProps } from './invoices-list.props.autogenerated';

export type InvoicesListStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'EMPTY';
export type InvoiceErrorHost = 'ADDINVOICETOCART';

export interface IInvoicesListViewProps extends IInvoicesListProps<IInvoicesListData> {
    moduleProps: IModuleProps;

    header: JSX.Element;
    filter: JSX.Element;
    content?: JSX.Element;
}

export interface IInvoiceErrorState {
    errorHost?: InvoiceErrorHost;
    errorReason?: IAddInvoicesToCartFailureResult;
    errorMessage: string;
    otherError?: string;
}

export interface IInvoicesListState {
    invoices: InvoiceModel[];
    invoicesFilterState: InvoicesFilterState;
    errorState: IInvoiceErrorState;
    isLoading: boolean;
}

/**
 * InvoicesList component
 * @extends {React.PureComponent<IInvoicesListProps<IInvoicesListData>, IInvoicesListState>}
 */
class InvoicesList extends React.Component<IInvoicesListProps<IInvoicesListData>, IInvoicesListState> {
    private readonly className: string = 'msc-invoices-list';

    constructor(props: IInvoicesListProps<IInvoicesListData>) {
        super(props);

        this._updateInvoicesState = this._updateInvoicesState.bind(this);
        this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        this._handleFilterStateChange = this._handleFilterStateChange.bind(this);

        this.state = {
            invoices: [],
            invoicesFilterState: InvoicesFilterState.ShowAll,
            isLoading: true,
            errorState: { errorMessage: '' }
        };
    }

    public render(): JSX.Element | null {
        const {
            data: { invoices, customer },
            config: { className = '' }
        } = this.props;

        const header = (
            <Heading
                className={`${this.className}__header`}
                text={this.props.resources.invoicesListTitle}
            />
        );

        const viewProps: IInvoicesListViewProps = {
            ...(this.props as IInvoicesListProps<IInvoicesListData>),
            moduleProps: {
                moduleProps: this.props,
                className: classnames(this.className, className)
            },
            header: header,
            filter: this._renderInvoicesFilter(this.className)
        };

        if (!invoices || !invoices.result || invoices?.status === 'LOADING' ||
            !customer || !customer.result || customer?.status === 'LOADING') {
            return this.props.renderView(viewProps);
        }

        if (ArrayExtensions.hasElements(invoices.result)) {
            viewProps.content = this._renderListContainer();
        } else {
            viewProps.content = this._renderEmptyInvoiceLinesPageContent();
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public async componentDidMount(): Promise<void> {
        const invoicesData = await this.props.data.invoices;
        this._initializeWithInvoices(invoicesData);
    }

    private _initializeWithInvoices(invoicesData: OrderInvoice[]): void {
        const invoices = invoicesData.map(invoice => new InvoiceModel(invoice));
        this.setState({
            invoices: invoices,
            isLoading: false
        });
    }

    private _renderEmptyInvoiceLinesPageContent(): JSX.Element {
        const containerClass = `${this.className}__empty-lines__container`;
        const actionsContainerClass = `${containerClass}__actions`;
        return (
            <div className={containerClass}>
                <div className={`${containerClass}__title`}>
                    {this.props.resources.invoiceEmptyInvoiceLinesTitle}
                </div>
                <div className={`${containerClass}__description`}>
                    {this.props.resources.invoiceEmptyInvoiceLinesDescription}
                </div>
                <div className={actionsContainerClass}>
                    <Button
                        className={`${actionsContainerClass}__continue-shopping`}
                        aria-label={this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonAriaLabel}
                        href={getUrlSync('home', this.props.context.actionContext)}
                    >
                        {this.props.resources.invoiceEmptyInvoiceLinesContinueShoppingButtonText}
                    </Button>
                </div>
            </div>
        );
    }

    private _renderListContainer(): JSX.Element {
        const containerClass = `${this.className}__container`;
        const containerHeaderClass = `${containerClass}__header`;
        const containerContentClass = `${containerClass}__content`;
        return (
            <div className={containerClass}>
                <div className={containerHeaderClass}>
                    {this._renderMakePayment(containerHeaderClass)}
                </div>
                <div className={containerContentClass}>
                    {this._renderInvoiceErrors()}
                </div>
                <div className={containerContentClass}>
                    {this._renderInvoicesList(containerContentClass)}
                </div>
            </div>
        );
    }

    private _updateInvoicesState(): void {
        this.setState({
            invoices: this.state.invoices
        });
    }

    private _handleFilterStateChange(state: InvoicesFilterState): void {
        this.setState({
            invoicesFilterState: state,
            isLoading: true
        });

        const searchCriteria: InvoiceSearchCriteria = {
            InvoiceTypeValues: [InvoiceType.SalesOrderInvoice]
        };

        switch (state) {
            case InvoicesFilterState.ShowAll:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.None];
                break;
            case InvoicesFilterState.Paid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Paid];
                break;
            case InvoicesFilterState.PartiallyPaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.PartiallyPaid];
                break;
            case InvoicesFilterState.Unpaid:
                searchCriteria.InvoicePaidStatusValues = [InvoicePaidStatus.Unpaid];
                break;
            default:
                throw new Error('Failed to process unknown invoices filter state.');
        }

        getInvoices(new GetInvoicesInput(searchCriteria), this.props.context.actionContext).then(result => {
            this._initializeWithInvoices(result);
        });
    }

    private _renderInvoicesFilter(className: string): JSX.Element {
        const filterClassName = `${className}__filter`;
        return (
            <InvoicesFilterComponent
                id={filterClassName}
                typeName={this.props.typeName}
                key={filterClassName}
                className={filterClassName}
                context={this.props.context}
                resources={this.props.resources}
                data={{}}
                currentState={InvoicesFilterState.ShowAll}
                onFilterStateChanged={this._handleFilterStateChange}
                disabled={this.state.isLoading}
            />
        );
    }

    private _renderInvoicesList(className: string): JSX.Element {
        const tableClassName = `${className}__table`;
        return (
            <InvoicesTableComponent
                id={tableClassName}
                typeName={this.props.typeName}
                key={tableClassName}
                className={tableClassName}
                data={{invoices: this.state.invoices, customer: this.props.data.customer.result!}}
                context={this.props.context}
                resources={this.props.resources}
                onChange={this._updateInvoicesState}
                onError={this._onAddToCartFailed}
            />
        );
    }

    private _onAddToCartFailed(newErrorState: IInvoiceErrorState): void {
        const errorState = {...newErrorState};
        errorState.errorHost = 'ADDINVOICETOCART';
        errorState.errorMessage = newErrorState.errorReason?.failureReason === 'EMPTYINPUT' ?
                    this.props.resources.invoiceErrorInvoiceIdNotExist : this.props.resources.invoiceErrorFailedAddInvoiceToCart;

        this.setState({errorState: errorState});
    }

    private _renderMakePayment(className: string): JSX.Element {
        const id = `${className}__make-payment`;
        const makePaymentResources: IMakePaymentResources = {
            makePaymentButtonText: this.props.resources.makePaymentButtonText,
            makePaymentButtonAriaLabel: this.props.resources.makePaymentButtonAriaLabel
        };

        let selectedInvoices = this.state.invoices.filter(invoice => invoice.isSelected);
        if (!ArrayExtensions.hasElements(selectedInvoices)) {
            selectedInvoices = this.state.invoices;
        }

        return (
            <MakePaymentComponent
                id={id}
                typeName={this.props.typeName}
                key={id}
                data={{invoices: selectedInvoices}}
                context={this.props.context}
                telemetry={this.props.telemetry}
                moduleId={this.props.id}
                moduleTypeName={this.props.typeName}
                navigationUrl={getUrlSync('cart', this.props.context.actionContext)}
                resources={makePaymentResources}
                onError={this._onAddToCartFailed}
            />
        );
    }

    private _renderInvoiceErrors(): JSX.Element {
        const errorClassName = `${this.className}__errors`;
        return (
            <InvoiceErrorComponent
                id={errorClassName}
                typeName={this.props.typeName}
                data={{}}
                messageHeader={this.props.resources.invoiceErrorMessageHeader}
                context={this.props.context}
                errorMessage={this.state.errorState.errorMessage}
                otherError={this.state.errorState.otherError}
                showError={this.state.errorState.errorHost === 'ADDINVOICETOCART'}
            />
        );
    }
}

export default InvoicesList;